import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {hideConstructionPhaseInfoModal, setRulesActiveSection} from '../../../redux/actions';
import {
  selectInitialRulesSectionId,
  selectShowConstructionPhaseInfoModal
} from '../../../redux/reducers/privateStateReducer/selectors';
import './RulesModal.css';
import PageButton from "../../elements/PageButton/PageButton";
import ModalButton from "../ModalWindow/ModalButton/ModalButton";
import {DEFAULT_RULES_SECTION_ID, RULES_SECTIONS} from "./rulesSections";
import {RULES_LEGEND_GROUPS} from "./rulesLegend";
import {MINUS_SIGN} from "../../../constants/text";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import TutorialRulesScrollHint from "../../Tutorial/TutorialRulesScrollHint/TutorialRulesScrollHint";
import {RulesNavigationContext} from "../../rules/RulesSectionLink/RulesSectionLink";

// Отступ сверху при переходе к месту в разделе (RulesSectionLink)
const RULES_ANCHOR_SCROLL_OFFSET_PX = 90;

const RulesModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectShowConstructionPhaseInfoModal);
  const initialRulesSectionId = useSelector(selectInitialRulesSectionId);
  const [isSectionsMenuOpen, setIsSectionsMenuOpen] = useState(false);
  // Широкий экран (>= 1344px): панель разделов закреплена слева и открыта
  // всегда — кнопка-бургер и выезжающий ящик не нужны (см. media-блок в
  // RulesModal.css). Класс --open при этом держится постоянно, поэтому
  // селекторы стрелок обучения (кандидаты с --open-скоупом) работают без
  // изменений: на широком экране первый кандидат виден сразу
  const [isWideLayout, setIsWideLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1344px)').matches
  );
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1344px)');
    const syncFromMediaQuery = () => setIsWideLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', syncFromMediaQuery);
    // Резерв: отдельные окружения (например, эмуляция вьюпорта в devtools)
    // не диспатчат событие change у matchMedia — обычный resize приходит
    // всегда, а setState с тем же значением ре-рендер не вызывает
    window.addEventListener('resize', syncFromMediaQuery);
    return () => {
      mediaQuery.removeEventListener('change', syncFromMediaQuery);
      window.removeEventListener('resize', syncFromMediaQuery);
    };
  }, []);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState(DEFAULT_RULES_SECTION_ID);
  // Место в разделе, к которому надо прокрутить после перехода по ссылке из
  // другого раздела (RulesSectionLink); без него раздел открывается с начала
  const [targetAnchorId, setTargetAnchorId] = useState(null);

  // Активный раздел дублируется в стор: обучение отслеживает задания вида
  // «открой раздел ...» (предикат rulesSectionOpened)
  useEffect(() => {
    if (isOpen) {
      dispatch(setRulesActiveSection(selectedSectionId));
    }
  }, [isOpen, selectedSectionId, dispatch]);
  const scrollAreaRef = useRef(null);

  // Кнопки внутри модалки звучат как обычные кнопки интерфейса
  // (playMenuButtonClick — тот же звук, что у PageButton/ModalButton по
  // умолчанию), а выезд и скрытие панели разделов — как боковые панели
  const playButtonSound = () => MENU_SOUND_MANAGER.playMenuButtonClick();
  const playSideMenuSound = () => MENU_SOUND_MANAGER.playSideMenuClick();

  const handleClose = () => {
    playSideMenuSound();
    dispatch(hideConstructionPhaseInfoModal());
  };

  const handleToggleLegend = (nextIsOpen) => {
    playButtonSound();
    setIsLegendOpen(nextIsOpen);
  };

  const handleToggleSectionsMenu = () => {
    playSideMenuSound();
    setIsSectionsMenuOpen((prevIsOpen) => !prevIsOpen);
  };

  const handleCloseSectionsMenu = () => {
    playSideMenuSound();
    setIsSectionsMenuOpen(false);
  };

  useEffect(() => {
    MENU_SOUND_MANAGER.playSideMenuClick();
  }, []);

  // Каждое новое открытие модалки стартует с запрошенного раздела (если он
  // передан вызывающей стороной — см. InfoButton, пункт «Правила» в InGameMenu) или с
  // раздела по умолчанию, и со скрытым боковым меню — а не с того, что было
  // выбрано в прошлый раз
  useEffect(() => {
    if (!isOpen) return;
    setSelectedSectionId(initialRulesSectionId || DEFAULT_RULES_SECTION_ID);
    setTargetAnchorId(null);
    setIsSectionsMenuOpen(false);
    setIsLegendOpen(false);
  }, [isOpen, initialRulesSectionId]);

  // При перелистывании раздела (стрелки в тулбаре или выбор в боковом меню)
  // контент подменяется на месте — скролл-позиция предыдущего раздела иначе
  // осталась бы как есть, и новый раздел мог открыться не с самого начала
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;
    const anchor = targetAnchorId && scrollArea.querySelector(`#${targetAnchorId}`);
    if (anchor) {
      // С отступом: поверх верха страницы лежат кнопки разделов и обозначений
      scrollArea.scrollTop += anchor.getBoundingClientRect().top - scrollArea.getBoundingClientRect().top
        - RULES_ANCHOR_SCROLL_OFFSET_PX;
      return;
    }
    scrollArea.scrollTop = 0;
  }, [selectedSectionId, targetAnchorId]);

  if (!isOpen) {
    return null;
  }

  // Звук у пунктов панели дает не PageButton (у них noSound), а обработчики —
  // иначе у пункта «Обозначения» он сложился бы со звуком handleToggleLegend
  const handleSelectSection = (sectionId) => {
    playButtonSound();
    setSelectedSectionId(sectionId);
    setTargetAnchorId(null);
    setIsSectionsMenuOpen(false);
  };

  // Переход по ссылке из текста раздела (RulesSectionLink)
  const handleNavigateToSection = (sectionId, anchorId = null) => {
    playButtonSound();
    setSelectedSectionId(sectionId);
    setTargetAnchorId(anchorId);
  };

  const currentSectionIndex = RULES_SECTIONS.findIndex((section) => section.id === selectedSectionId);
  const activeSection = RULES_SECTIONS[currentSectionIndex] || RULES_SECTIONS[0];
  const ActiveSectionComponent = activeSection.Component;
  const hasPrevSection = currentSectionIndex > 0;
  const hasNextSection = currentSectionIndex >= 0 && currentSectionIndex < RULES_SECTIONS.length - 1;

  const handlePrevSection = () => {
    if (!hasPrevSection) return;
    setSelectedSectionId(RULES_SECTIONS[currentSectionIndex - 1].id);
    setTargetAnchorId(null);
  };

  const handleNextSection = () => {
    if (!hasNextSection) return;
    setSelectedSectionId(RULES_SECTIONS[currentSectionIndex + 1].id);
    setTargetAnchorId(null);
  };

  return (
    <div className="rules-modal_overlay" onClick={handleClose}>
      <div
        className="rules-modal standard-background"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleToggleSectionsMenu}
          data-tutorial-select={'rules_sections_toggle'}
          className={'rules-modal_menu-button standard-background'}
        >
          <img
            src={'/img/icons/menu_controls.png'}
            alt={'Разделы правил'}
          />
        </button>

        <button
          onClick={() => handleToggleLegend(true)}
          className={'rules-modal_legend-button standard-background'}
          title={'Условные обозначения'}
        >
          <img
            src={'/img/icons/question.png'}
            alt={'Условные обозначения'}
          />
        </button>

        {/* Именно оверлей поверх контента, а не подмена раздела: текущая
            страница правил остается смонтированной, и после закрытия подсказки
            пользователь видит то же место и ту же позицию прокрутки */}
        {isLegendOpen && (
          <div className="rules-modal_legend standard-background">
            <button
              onClick={() => handleToggleLegend(false)}
              className={'rules-modal_legend-close-button standard-background'}
              title={'Закрыть'}
            >
              <img
                src={'/img/icons/exit.png'}
                alt={'Закрыть'}
              />
            </button>

            <div className="rules-modal_legend-scroll-area">
              <h2 className="rules-modal_legend-title">Условные обозначения</h2>

              {RULES_LEGEND_GROUPS.map((group) => (
                <div className="rules-modal_legend-group" key={group.title}>
                  <h3 className="rules-modal_legend-group-title">{group.title}</h3>

                  {/* Плоская группа — тот же путь, что и группа с подзаголовками:
                      просто одна безымянная подгруппа */}
                  {(group.subGroups || [{items: group.items}]).map((subGroup, subGroupIndex) => (
                    <div className="rules-modal_legend-subgroup" key={subGroup.title || subGroupIndex}>
                      {subGroup.title && (
                        <h4 className="rules-modal_legend-subgroup-title">{subGroup.title}</h4>
                      )}

                      <div className="rules-modal_legend-list">
                        {subGroup.items.map((item) => (
                          <div className="rules-modal_legend-row" key={`${group.title}_${item.src}`}>
                            <span className="rules-modal_legend-icon-box">
                              <img
                                className={`rules-modal_legend-icon${item.isUnitIcon ? ' rules-modal_legend-icon_unit' : ''} ${item.className}`}
                                src={item.src}
                                alt={item.label}
                              />
                            </span>
                            <span className="rules-modal_legend-label">{MINUS_SIGN} {item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rules-modal_scroll-area" ref={scrollAreaRef}>
          <RulesNavigationContext.Provider value={handleNavigateToSection}>
            <ActiveSectionComponent/>
          </RulesNavigationContext.Provider>
        </div>

        {/* Подсказка обучения «пролистай страницу» — поверх страницы раздела:
            под оверлеем правил не видно ни обучающих модалок, ни панели задач,
            ни стрелки (см. TutorialRulesScrollHint) */}
        <TutorialRulesScrollHint
          scrollAreaRef={scrollAreaRef}
          sectionId={selectedSectionId}
          isHidden={isLegendOpen}
        />

        {/* Сестра скролл-области, а не ее содержимое: в колонке-флексе
            .rules-modal скроллится только она, а тулбар остается прижатым к низу
            модалки и доступен без прокрутки длинного раздела */}
        <div className="rules-modal_nav-row">
          <ModalButton
            className={'rules-modal_nav-button'}
            onClick={handlePrevSection}
            disabled={!hasPrevSection}
          >
            <span
              className={'rules-modal_nav-button-icon'}
              style={{
                WebkitMaskImage: "url('/img/icons/arrow left.png')",
                maskImage: "url('/img/icons/arrow left.png')",
              }}
              role={'img'}
              aria-label={'Предыдущий раздел'}
            />
          </ModalButton>

          <ModalButton
            className={'rules-modal_close-button'}
            onClick={handleClose}
            text={'Скрыть'}
            noSound={true}
          />

          <ModalButton
            className={'rules-modal_nav-button'}
            onClick={handleNextSection}
            disabled={!hasNextSection}
          >
            <span
              className={'rules-modal_nav-button-icon'}
              style={{
                WebkitMaskImage: "url('/img/icons/arrow right.png')",
                maskImage: "url('/img/icons/arrow right.png')",
              }}
              role={'img'}
              aria-label={'Следующий раздел'}
            />
          </ModalButton>
        </div>

        <div
          className={`rules-modal_sections-backdrop ${isSectionsMenuOpen ? 'rules-modal_sections-backdrop--open' : ''}`}
          onClick={handleCloseSectionsMenu}
        />

        <div
          className={`rules-modal_sections-panel ${isSectionsMenuOpen || isWideLayout ? 'rules-modal_sections-panel--open' : ''}`}
        >
          {RULES_SECTIONS.map((section) => (
            <PageButton
              key={section.id}
              data-tutorial-select={`rules_section_${section.id}`}
              className={`rules-modal_sections-panel_link ${
                section.id === selectedSectionId ? 'rules-modal_sections-panel_link--active' : ''
              }`}
              onClick={() => handleSelectSection(section.id)}
              noSound={true}
            >
              <span className="rules-modal_sections-panel_link-icon-box">
                {section.iconSrc && (
                  <img
                    className={`rules-modal_sections-panel_link-icon ${section.iconClassName}`}
                    src={section.iconSrc}
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </span>
              <span className="rules-modal_sections-panel_link-label">{section.label}</span>
            </PageButton>
          ))}

          {/* Не раздел правил, а тот же оверлей, что открывает кнопка «?»:
              панель при этом закрывается, иначе осталась бы открытой под ним */}
          <PageButton
            className={'rules-modal_sections-panel_link'}
            onClick={() => {
              setIsSectionsMenuOpen(false);
              handleToggleLegend(true);
            }}
            noSound={true}
          >
            <span className="rules-modal_sections-panel_link-icon-box">
              <img
                className="rules-modal_sections-panel_link-icon"
                src={'/img/icons/question.png'}
                alt=""
                aria-hidden="true"
              />
            </span>
            <span className="rules-modal_sections-panel_link-label">Обозначения</span>
          </PageButton>
        </div>
      </div>
    </div>
  );
};

export default RulesModal;
